import React from "react";
import styled, { css, keyframes } from "styled-components";

import type { SoremonMood } from "./soremonStore";

/**
 * soremon のキャラクター（体験仕様「7〜15」）。
 *
 * 白い光を内包した小さな生命体。乳白色の半透明の体が内側から柔らかく光り、
 * 顔は小さな目だけ。状態（mood）は発光・視線・輪郭の動きで伝える。
 * 画像に頼らずコードで描くので、イラストや 3D に差し替えるときはこの
 * コンポーネントの中身だけを置き換えればよい（props は据え置く）。
 */
export interface SoremonCharacterProps {
  mood?: SoremonMood;
  size?: "small" | "medium" | "large";
  className?: string;
}

const SIZE_PX = { small: 32, medium: 40, large: 72 } as const;

// ── 動き ──

/* 呼吸するような、ごくわずかな明滅と伸縮 */
const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.035); }
`;
const glowBreathe = keyframes`
  0%, 100% { opacity: var(--glow-min); }
  50% { opacity: var(--glow-max); }
`;
/* 波形のような輪郭の揺らぎ（左右非対称の有機的な形を保つ） */
const morph = keyframes`
  0%, 100% { border-radius: 47% 53% 50% 50% / 54% 50% 50% 46%; }
  33% { border-radius: 51% 49% 46% 54% / 50% 54% 46% 50%; }
  66% { border-radius: 49% 51% 53% 47% / 47% 49% 51% 53%; }
`;
const blink = keyframes`
  0%, 93%, 100% { transform: scaleY(1); }
  95% { transform: scaleY(0.12); }
`;
/* Thinking: 内部の光がゆっくり移動する */
const drift = keyframes`
  0%, 100% { transform: translate(-12%, -10%); }
  25% { transform: translate(10%, -14%); }
  50% { transform: translate(12%, 8%); }
  75% { transform: translate(-10%, 12%); }
`;
const glance = keyframes`
  0%, 40%, 100% { transform: translate(0, -6%); }
  50%, 85% { transform: translate(14%, -10%); }
`;
/* Acting: 光が外へ流れる */
const flow = keyframes`
  0% { transform: scale(0.9); opacity: 0.55; }
  100% { transform: scale(1.7); opacity: 0; }
`;
/* Completed: 一度柔らかく発光して戻る */
const flash = keyframes`
  0% { opacity: var(--glow-max); transform: scale(1); }
  35% { opacity: 1; transform: scale(1.35); }
  100% { opacity: var(--glow-min); transform: scale(1); }
`;

// ── 状態ごとの見え方 ──

interface MoodStyle {
  glowMin: number;
  glowMax: number;
  breatheSeconds: number;
  /** 目の位置（ユーザー＝下側の入力欄を見る、など） */
  eyeOffset: string;
  eyeScale: string;
  dim: boolean;
}

const MOOD_STYLE: Record<SoremonMood, MoodStyle> = {
  idle: { glowMin: 0.35, glowMax: 0.6, breatheSeconds: 5.2, eyeOffset: "0, 0", eyeScale: "1", dim: false },
  listening: { glowMin: 0.55, glowMax: 0.8, breatheSeconds: 4.2, eyeOffset: "-6%, 10%", eyeScale: "1", dim: false },
  thinking: { glowMin: 0.5, glowMax: 0.85, breatheSeconds: 2.8, eyeOffset: "0, -6%", eyeScale: "1", dim: false },
  acting: { glowMin: 0.75, glowMax: 1, breatheSeconds: 1.8, eyeOffset: "0, 0", eyeScale: "1, 0.72", dim: false },
  asking: { glowMin: 0.6, glowMax: 0.75, breatheSeconds: 4.6, eyeOffset: "-6%, 12%", eyeScale: "1.15", dim: false },
  completed: { glowMin: 0.55, glowMax: 0.85, breatheSeconds: 4.2, eyeOffset: "0, 0", eyeScale: "1.1, 0.55", dim: false },
  error: { glowMin: 0.12, glowMax: 0.22, breatheSeconds: 6.5, eyeOffset: "0, 14%", eyeScale: "1, 0.6", dim: true },
};

const Root = styled.span<{ $px: number; $mood: SoremonMood }>`
  --glow-min: ${({ $mood }) => MOOD_STYLE[$mood].glowMin};
  --glow-max: ${({ $mood }) => MOOD_STYLE[$mood].glowMax};
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  width: ${({ $px }) => $px}px;
  height: ${({ $px }) => $px}px;
  ${({ $mood }) =>
    MOOD_STYLE[$mood].dim &&
    css`
      filter: saturate(0.4) brightness(0.92);
    `}
  transition: filter 0.6s ease;

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation: none !important;
    }
  }
`;

/* 体の外へにじむ光（乳白色のシェード越しに拡散する光） */
const Halo = styled.span<{ $mood: SoremonMood }>`
  position: absolute;
  inset: -22%;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 248, 232, 0.95) 0%,
    rgba(255, 244, 222, 0.5) 38%,
    rgba(255, 240, 215, 0) 70%
  );
  filter: blur(2px);
  pointer-events: none;
  animation: ${glowBreathe} ${({ $mood }) => MOOD_STYLE[$mood].breatheSeconds}s ease-in-out infinite;
  ${({ $mood }) =>
    $mood === "completed" &&
    css`
      animation: ${flash} 1.2s ease-out 1;
    `}
`;

const Ring = styled.span`
  position: absolute;
  inset: 6%;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 236, 200, 0.9);
  box-shadow: 0 0 6px rgba(255, 232, 190, 0.8);
  pointer-events: none;
  animation: ${flow} 1.4s ease-out infinite;
`;

const Body = styled.span<{ $mood: SoremonMood }>`
  position: absolute;
  inset: 8%;
  overflow: hidden;
  border-radius: 47% 53% 50% 50% / 54% 50% 50% 46%;
  /* 乳白色の半透明素材。内側（やや左上）ほど明るく、縁はわずかに影を帯びる */
  background: radial-gradient(
    circle at 42% 36%,
    #ffffff 0%,
    #fdfbf6 34%,
    #f3efe6 68%,
    #e7e2d7 100%
  );
  box-shadow:
    inset 0 -3px 6px rgba(120, 104, 80, 0.12),
    inset 0 2px 5px rgba(255, 255, 255, 0.9),
    0 1px 4px rgba(0, 0, 0, 0.1),
    0 0 0 0.5px rgba(0, 0, 0, 0.06);
  animation:
    ${breathe} ${({ $mood }) => MOOD_STYLE[$mood].breatheSeconds}s ease-in-out infinite,
    ${morph} 9s ease-in-out infinite;
`;

/* 内部の光。Thinking ではゆっくり移動し、Acting では強くなる */
const Core = styled.span<{ $mood: SoremonMood }>`
  position: absolute;
  inset: 12%;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 252, 240, 1) 0%,
    rgba(255, 246, 225, ${({ $mood }) => ($mood === "acting" ? 0.9 : 0.55)}) 40%,
    rgba(255, 246, 225, 0) 72%
  );
  opacity: ${({ $mood }) => ($mood === "error" ? 0.35 : 0.9)};
  transform: translate(-12%, -10%);
  transition: opacity 0.6s ease;
  ${({ $mood }) =>
    ($mood === "thinking" || $mood === "acting") &&
    css`
      animation: ${drift} ${$mood === "acting" ? 1.6 : 3.4}s ease-in-out infinite;
    `}
`;

const Eyes = styled.span<{ $mood: SoremonMood }>`
  position: absolute;
  left: 50%;
  top: 46%;
  display: flex;
  gap: 22%;
  width: 44%;
  justify-content: center;
  translate: -50% -50%;
  transform: translate(${({ $mood }) => MOOD_STYLE[$mood].eyeOffset});
  transition: transform 0.5s ease;
  ${({ $mood }) =>
    $mood === "thinking" &&
    css`
      animation: ${glance} 4.2s ease-in-out infinite;
    `}
`;

const Eye = styled.span<{ $mood: SoremonMood }>`
  display: block;
  width: 17%;
  aspect-ratio: 0.72;
  min-width: 2.5px;
  border-radius: 50%;
  background: #2f2d2a;
  scale: ${({ $mood }) => MOOD_STYLE[$mood].eyeScale};
  transition: scale 0.4s ease;
  transform-origin: center 60%;
  animation: ${blink} ${({ $mood }) => ($mood === "error" ? 8 : 5.6)}s ease-in-out infinite;
`;

const SoremonCharacter: React.FC<SoremonCharacterProps> = ({
  mood = "idle",
  size = "medium",
  className,
}) => (
  <Root
    $px={SIZE_PX[size]}
    $mood={mood}
    className={className}
    role="img"
    aria-label="soremon"
    data-mood={mood}
  >
    <Halo $mood={mood} />
    {mood === "acting" && <Ring />}
    <Body $mood={mood}>
      <Core $mood={mood} />
      <Eyes $mood={mood}>
        <Eye $mood={mood} />
        <Eye $mood={mood} />
      </Eyes>
    </Body>
  </Root>
);

export default SoremonCharacter;
